import { postJSON } from "../api.js";
import { esc } from "../ui/format.js";
import { ui3DBars, uiFinding, uiGet, uiGhostPanel, uiKpis, uiMeter, uiMoney, uiPct, uiPill } from "../ui/kit.js";
import { toast } from "../ui/modals.js";
import { _wireOptionCardClicks, renderConfigDimension } from "./core/operations.js";

/**
 * Launch Product — the Product Studio flow (replaces app.js's single-page
 * launch form).
 *
 * Four steps, in the order a real product team decides:
 *   1. Product type: the industry's five types, each with a brief (what it
 *      is, who buys, how it earns, what it looks like, what kills it), the
 *      development budget it needs and its projection; the best one for
 *      this company is flagged.
 *   2. Specifications: the other four dimensions, each option with its own
 *      numbers, its fit with the type, and the best pick flagged.
 *   3. Go-to-market: the industry's marketing plan and budget.
 *   4. Review: name, budget, and the backend's own launch estimate.
 * Every number comes from /api/products/studio or /products/launch-estimate.
 */

const LX_STEPS = ["Product type", "Specifications", "Go-to-market", "Review & launch"];

function lxProjectionStats(p) {
  return `
    <div class="lx-stats">
      <div><span>Starting revenue</span><b>${uiMoney(p.start_monthly_revenue, { compact: true })}/mo</b></div>
      <div><span>Growth</span><b>${uiPct(p.growth_pct, 2)}/mo</b></div>
      <div><span>First-year revenue</span><b>${uiMoney(p.first_year_revenue, { compact: true })}</b></div>
      <div><span>Revenue per $1 built</span><b>$${(p.revenue_per_dollar || 0).toFixed(2)}</b></div>
    </div>`;
}

/** A budget slider: drag to set, the effect updates live. `marker` pins a
 *  reference amount (e.g. the build's required budget) on the track. */
function lxSlider(id, label, value, max, { marker = null, markerLabel = "" } = {}) {
  const top = Math.max(1000, Math.ceil(max / 1000) * 1000);
  const step = Math.max(1000, Math.round(top / 400 / 1000) * 1000);
  const v = Math.min(top, Math.max(0, Math.round(value || 0)));
  const markPct = marker !== null && marker !== undefined ? Math.min(100, (marker / top) * 100) : null;
  return `
    <div class="lx-slider">
      <div class="lx-slider-head"><span>${esc(label)}</span><b id="${id}-val">${uiMoney(v)}</b></div>
      <div class="lx-slider-track">
        <input type="range" id="${id}" min="0" max="${top}" step="${step}" value="${v}" aria-label="${esc(label)}"/>
        ${markPct !== null ? `<div class="lx-slider-mark" style="left:${markPct.toFixed(2)}%"><span>${esc(markerLabel)}</span></div>` : ""}
      </div>
      <div class="lx-slider-scale"><span>$0</span><span>${uiMoney(top, { compact: true })}</span></div>
    </div>`;
}

function lxTypeCard(t) {
  const funding = t.affordable
    ? uiPill("Fully fundable", "good")
    : uiPill(`${t.funding_pct_at_cap}% fundable`, t.funding_pct_at_cap >= 60 ? "warn" : "bad");
  return `
    <button type="button" class="lx-type ${t.recommended ? "best" : ""}" data-type="${t.index}">
      ${t.recommended ? `<div class="lx-ribbon">Best for you</div>` : ""}
      <div class="lx-type-head">
        <span class="sx-eyebrow">${esc(t.tier_label)}</span>
        <h4>${esc(t.label)}</h4>
      </div>
      <p class="lx-what">${esc(t.what)}</p>
      <dl class="lx-brief">
        <dt>Who buys</dt><dd>${esc(t.buyer)}</dd>
        <dt>How it earns</dt><dd>${esc(t.earns)}</dd>
        <dt>In the market</dt><dd>${esc(t.example)}</dd>
        <dt>What kills it</dt><dd>${esc(t.risk)}</dd>
      </dl>
      <div class="lx-budget"><span>Development budget</span><b>${uiMoney(t.required_budget, { compact: true })}</b>${funding}</div>
      ${lxProjectionStats(t.projection)}
      <div class="lx-foot">Projected at ${uiMoney(t.projected_at_spend, { compact: true })} spend</div>
    </button>`;
}

function lxOptionCard(dimIdx, o) {
  const fitTone = o.fit === "natural fit" ? "good" : o.fit === "stretch" ? "warn" : "bad";
  const delta = o.budget_delta;
  return `
    <button type="button" class="lx-opt ${o.selected ? "selected" : ""} ${o.recommended ? "best" : ""}" data-dim="${dimIdx}" data-opt="${o.index}">
      <div class="lx-opt-top"><span class="sx-eyebrow">${esc(o.tier_label)}</span>${o.recommended ? uiPill("Best", "info") : ""}</div>
      <div class="lx-opt-label">${esc(o.label)}</div>
      <div class="lx-opt-narr">${esc(o.narrative)}</div>
      <div class="lx-opt-pills">${uiPill(o.fit + (o.fit_penalty_pct ? ` −${o.fit_penalty_pct}%` : ""), fitTone)}
        ${delta ? uiPill(`${delta > 0 ? "+" : "−"}${uiMoney(Math.abs(delta), { compact: true })} budget`, delta > 0 ? "warn" : "good") : uiPill("budget ±0")}</div>
      <div class="lx-opt-proj"><span>1st-year rev.</span><b>${uiMoney(o.projection.first_year_revenue, { compact: true })}</b></div>
    </button>`;
}

async function openLaunchProductModal(opts = {}) {
  const st = { step: 0, typeIndex: null, selections: null, autoBudget: true, investment: null,
               marketingBudget: null, name: opts.innovation ? opts.innovation.title.split(" · ")[0] : "", mktSel: {} };
  let base = null, studio = null, mktSchema = null, seq = 0;

  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop";
  backdrop.innerHTML = `
    <div class="modal modal-page lx">
      <div class="modal-page-header lx-header">
        <div><h3>${opts.innovation ? `Launch a product from your R&amp;D: ${esc(opts.innovation.title)}` : "Launch a Product"}</h3><p class="lx-sub" id="lx-sub"></p>
          ${opts.innovation ? `<p class="lx-rnd">💡 Built on your research: ${esc(opts.innovation.launch_bonus)}.</p>` : ""}</div>
        <ol class="lx-steps" id="lx-steps"></ol>
      </div>
      <div class="modal-page-body" id="lx-body"><div class="lx-loading">Loading your industry's product types…</div></div>
      <div class="modal-page-footer">
        <div class="lx-footnote" id="lx-footnote"></div>
        <div class="modal-actions">
          <button id="lx-cancel">Cancel</button>
          <button id="lx-back">Back</button>
          <button class="primary" id="lx-next">Next</button>
        </div>
      </div>
    </div>`;
  document.body.appendChild(backdrop);
  const $b = (sel) => backdrop.querySelector(sel);
  const close = () => backdrop.remove();
  $b("#lx-cancel").addEventListener("click", close);

  const fetchStudio = async () => {
    const my = ++seq;
    const body = { type_index: st.typeIndex, selections: st.selections,
                   investment: st.autoBudget ? null : st.investment, marketing_selections: st.mktSel };
    const res = await postJSON("/products/studio", body);
    if (my !== seq) return null;
    return res;
  };

  const renderSteps = () => {
    $b("#lx-steps").innerHTML = LX_STEPS.map((s, i) =>
      `<li class="${i === st.step ? "on" : i < st.step ? "done" : ""}"><span>${i + 1}</span>${esc(s)}</li>`).join("");
    $b("#lx-back").style.visibility = st.step === 0 ? "hidden" : "visible";
    $b("#lx-next").textContent = st.step === 3 ? "Launch" : "Next";
    $b("#lx-next").disabled = st.step === 0 && st.typeIndex === null;
  };

  // ------------------------------------------------------------ step 1
  /**
   * The fast path (Phase 10, first 3 minutes): the recommended type, every
   * best spec pick and the default marketing, straight to the review step.
   * The player still sees the full numbers and confirms the launch.
   */
  const fastLaunch = async () => {
    st.typeIndex = base.recommended_type;
    st.selections = null;
    const first = await fetchStudio();
    if (!first) return;
    st.selections = { ...first.best_selections };
    studio = await fetchStudio();
    if (!studio) return;
    st.selections = studio.selections;
    st.marketingBudget = Math.min(Math.round(studio.investment * 0.2 / 1000) * 1000, Math.max(0, Math.floor(base.cash * 0.1 / 1000) * 1000));
    await go(3);
  };

  const renderTypes = () => {
    $b("#lx-sub").textContent = `Step 1: choose what kind of ${base.defining_dimension.toLowerCase()} you are building. Everything else is specified against it.`;
    const rec = base.types[base.recommended_type];
    $b("#lx-body").innerHTML = `
      ${rec ? `<div class="lx-fast${base.has_products ? "" : " first"}">
        <div><b>⚡ ${base.has_products ? "In a hurry?" : "Your first launch"}</b><span>Launch <b>${esc(rec.label)}</b> with every recommended spec and a starter marketing budget. You'll see the full numbers before you confirm.</span></div>
        <button type="button" class="primary" id="lx-fast">Launch the recommended product</button></div>` : ""}`;
    $b("#lx-body").innerHTML += `
      <div class="lx-intro">
        <div class="lx-intro-kpis">
          ${uiKpis([
            { label: "Cash on hand", value: uiMoney(base.cash, { compact: true }) },
            { label: "Launch budget available", value: uiMoney(base.launch_budget_cap, { compact: true }), sub: "60% of cash, keeping a reserve" },
            { label: "Industry build-cost factor", value: `×${base.capital_factor}`, sub: "capital intensity of your sector" },
          ])}
        </div>
        ${ui3DBars(base.types.map((t) => ({ label: t.label, value: t.projection.first_year_revenue, highlight: t.recommended,
                                            sub: `$${t.projection.revenue_per_dollar.toFixed(2)} per $1` })),
                   { caption: "First-year revenue by product type (at the budget you can fund)" })}
      </div>
      <div class="lx-rules">
        <div><b>Budget.</b> ${esc(base.rules.budget)}</div>
        <div><b>Fit.</b> ${esc(base.rules.fit)}</div>
        <div><b>Best for you.</b> ${esc(base.rules.best)}</div>
      </div>
      <div class="lx-types">${base.types.map(lxTypeCard).join("")}</div>
      <div class="lx-bp"><label for="lx-bp">Have a blueprint code?</label><input id="lx-bp" placeholder="BP-XXXXXX" maxlength="12" autocomplete="off">
        <button type="button" id="lx-bp-go">Load blueprint</button><span id="lx-bp-msg" aria-live="polite"></span></div>
      <p class="lx-note">${esc(base.projection_note)}</p>`;
    // Forge blueprints: a share code rebuilds a whole configuration (backend/blueprints.py)
    $b("#lx-bp-go").addEventListener("click", async () => {
      const msg = $b("#lx-bp-msg");
      try {
        const bp = await uiGet(`/forge/blueprint/${encodeURIComponent($b("#lx-bp").value.trim())}`);
        if (!bp.fits) { msg.textContent = bp.note; msg.className = "neg"; return; }
        st.selections = { ...bp.selections };
        st.typeIndex = Number(bp.selections[base.defining_dimension]);
        msg.textContent = `Loaded ${bp.code}${bp.combos.length ? ` · combos: ${bp.combos.join(", ")}` : ""}`;
        msg.className = "pos";
        await go(1);
      } catch (e) { msg.textContent = e.message || "That code didn't work."; msg.className = "neg"; }
    });
    const fb = $b("#lx-fast");
    if (fb) fb.addEventListener("click", async () => { fb.disabled = true; try { await fastLaunch(); } catch (e) { fb.disabled = false; toast(e.message || "Couldn't prepare the launch.", true); } });
    backdrop.querySelectorAll(".lx-type").forEach((el) => el.addEventListener("click", () => {
      const idx = Number(el.dataset.type);
      if (st.typeIndex !== idx) { st.selections = null; st.autoBudget = true; st.investment = null; }
      st.typeIndex = idx;
      backdrop.querySelectorAll(".lx-type").forEach((x) => x.classList.toggle("selected", x === el));
      renderSteps();
      $b("#lx-footnote").innerHTML = `Selected: <b>${esc(base.types[idx].label)}</b>`;
    }));
    if (st.typeIndex !== null) backdrop.querySelector(`.lx-type[data-type="${st.typeIndex}"]`)?.classList.add("selected");
  };

  // ------------------------------------------------------------ step 2
  const summaryPanel = () => {
    const f = studio.funding, fit = studio.fit;
    return `
      <aside class="lx-summary">
        <div class="sx-eyebrow">Your build</div>
        <h4>${esc(base.types[st.typeIndex].label)}</h4>
        ${uiMeter(studio.investment, studio.required_budget, { label: "Development budget" })}
        <div class="lx-sum-line">${f.funding_ratio >= 1
          ? `<span class="pos">Fully funded.</span>`
          : `<span class="neg">Underfunded (${Math.round(f.funding_ratio * 100)}%).</span> Ships at ${Math.round(f.quality * 100)}% of its specified sophistication.`}</div>
        <div class="lx-sum-line">${fit.coherent ? `<span class="pos">Coherent: every spec fits the type (+4%).</span>`
          : `<span class="neg">Fit ×${fit.factor.toFixed(2)}:</span> ${fit.specs.filter((s) => s.gap > 1).map((s) => esc(s.dimension)).join(", ")} ${fit.specs.filter((s) => s.gap > 1).length === 1 ? "doesn't" : "don't"} match the type.`}</div>
        ${lxProjectionStats(studio.projection)}
        ${lxSlider("lx-inv", "Development budget", studio.investment, base.cash, { marker: studio.required_budget, markerLabel: "needed" })}
        <div class="lx-auto">${st.autoBudget ? `<span class="pos">Sized to the build automatically.</span> Drag to set your own.`
          : `<button type="button" class="lx-link" id="lx-auto">Size to the build automatically</button>`}</div>
        <button type="button" class="primary" id="lx-best">Use every best pick</button>
        <div id="lx-forge"></div>
      </aside>`;
  };
  const renderSpecs = () => {
    $b("#lx-sub").textContent = "Step 2: specify the product. Each option shows what it adds to the budget, how well it fits the type, and its first-year revenue.";
    $b("#lx-body").innerHTML = `
      <div class="lx-spec-layout">
        <div class="lx-dims">${studio.specs.map((d, i) => `
          <section class="lx-dim">
            <h4>${esc(d.dimension)}</h4>
            <p class="lx-guide">${esc(d.guide)}</p>
            <div class="lx-opts">${d.options.map((o) => lxOptionCard(i, o)).join("")}</div>
          </section>`).join("")}</div>
        ${summaryPanel()}
      </div>`;
    backdrop.querySelectorAll(".lx-opt").forEach((el) => el.addEventListener("click", async () => {
      const dim = studio.specs[Number(el.dataset.dim)].dimension;
      st.selections = { ...studio.selections, [dim]: el.dataset.opt };
      await reloadSpecs();
    }));
    $b("#lx-best").addEventListener("click", async () => { st.selections = { ...studio.best_selections }; await reloadSpecs(); });
    $b("#lx-auto")?.addEventListener("click", async () => { st.autoBudget = true; st.investment = null; await reloadSpecs(); });
    // Dragging shows the amount at once; releasing re-prices every option.
    $b("#lx-inv").addEventListener("input", (e) => { $b("#lx-inv-val").textContent = uiMoney(Number(e.target.value)); });
    $b("#lx-inv").addEventListener("change", async (e) => {
      st.autoBudget = false; st.investment = Number(e.target.value) || 0; await reloadSpecs();
    });
    $b("#lx-footnote").innerHTML = `Budget needed: <b>${uiMoney(studio.required_budget)}</b> · building with ${uiMoney(studio.investment)}`;
    // The Product Forge's live read: combos, clash warnings, customers, rival.
    if (typeof window.fgDecorate === "function") window.fgDecorate(backdrop, studio).then((f) => { if (f) st.codename = f.codename; });
  };
  const reloadSpecs = async () => {
    const res = await fetchStudio();
    if (!res) return;
    studio = res;
    st.selections = res.selections;
    const scroll = $b("#lx-body").scrollTop;
    renderSpecs();
    $b("#lx-body").scrollTop = scroll;
  };

  // ------------------------------------------------------------ step 3
  const renderMarketing = () => {
    $b("#lx-sub").textContent = "Step 3: plan the go-to-market. Marketing is expensed immediately and buys a launch boost sized by dollars and targeting.";
    if (st.marketingBudget === null) {
      st.marketingBudget = Math.min(Math.round(studio.investment * 0.2 / 1000) * 1000, Math.max(0, Math.floor(base.cash * 0.1 / 1000) * 1000));
    }
    $b("#lx-body").innerHTML = `
      <div id="lx-mkt">${mktSchema.categories.map((cat, idx) =>
        renderConfigDimension(cat, idx, "mktg", st.mktSel[cat.category] || "1", "marketing plan")).join("")}</div>
      <div class="lx-mkt-budget">
        ${lxSlider("lx-mbudget", "Marketing budget", st.marketingBudget, Math.max(10000, base.cash * 0.5))}
        <div class="lx-effect" id="lx-mkt-effect"></div>
      </div>`;
    const collect = () => {
      mktSchema.categories.forEach((cat, idx) => {
        const checked = backdrop.querySelector(`input[name="mktg-${idx}"]:checked`);
        st.mktSel[cat.category] = checked ? checked.value : "1";
      });
    };
    let timer = null, my = 0;
    const effect = () => {
      clearTimeout(timer);
      timer = setTimeout(async () => {
        const n = ++my;
        const est = await postJSON("/products/launch-estimate", { investment: devBudget(), marketing_budget: st.marketingBudget || 0,
          selections: studio.selections, marketing_selections: st.mktSel }).catch(() => null);
        if (!est || n !== my || !$b("#lx-mkt-effect")) return;
        $b("#lx-mkt-effect").innerHTML = est.marketing_budget > 0
          ? `<div><span>Launch boost</span><b>+${est.launch_marketing_boost_pct.toFixed(1)}%/mo</b></div>
             <div><span>Growth with this plan</span><b>${est.est_monthly_growth_rate_pct.toFixed(2)}%/mo</b></div>
             <div><span>Total launch cost</span><b>${uiMoney(est.total_launch_cost, { compact: true })}</b></div>
             ${est.scaled_to_cash ? `<div class="neg">More than your cash: the launch will be scaled down.</div>` : ""}`
          : `<div><span>Launch boost</span><b>none</b></div><div class="lx-foot">No marketing: the product relies on word of mouth.</div>`;
      }, 120);
    };
    _wireOptionCardClicks($b("#lx-mkt"), () => { collect(); effect(); });
    collect();
    $b("#lx-mbudget").addEventListener("input", (e) => {
      st.marketingBudget = Number(e.target.value) || 0;
      $b("#lx-mbudget-val").textContent = uiMoney(st.marketingBudget);
      effect();
    });
    effect();
    $b("#lx-footnote").textContent = "";
  };

  // ------------------------------------------------------------ step 4
  const devBudget = () => (st.autoBudget ? studio.investment : st.investment);
  const renderEstimate = async () => {
    const my = ++seq;
    const est = await postJSON("/products/launch-estimate", {
      investment: devBudget(), marketing_budget: st.marketingBudget || 0,
      selections: studio.selections, marketing_selections: st.mktSel,
    });
    if (my !== seq || !$b("#lx-est")) return;
    const s = est.studio || {};
    const warnings = [];
    if (est.scaled_to_cash) warnings.push({ severity: 3, title: "Scaled down to your cash",
      detail: `You asked for ${uiMoney(est.requested_investment + est.requested_marketing_budget)} but have ${uiMoney(est.available_cash)}.` });
    if (s.funding_ratio !== undefined && s.funding_ratio < 1) warnings.push({ severity: 2, title: `Underfunded at ${Math.round(s.funding_ratio * 100)}%`,
      detail: `The build needs ${uiMoney(s.required_budget)}. It will ship at ${Math.round(s.quality * 100)}% of its specified sophistication and ramp slower.` });
    if (s.fit_factor !== undefined && s.fit_factor < 1) warnings.push({ severity: 2, title: "Specs don't fit the type",
      detail: `Launch revenue ×${s.fit_factor.toFixed(2)}. Go back to Specifications to see which ones.` });
    if (!warnings.length) warnings.push({ severity: 0, title: "Ready to launch", detail: "Fully funded, and every spec fits the product type." });
    $b("#lx-est").innerHTML = `${uiKpis([
        { label: "Starting revenue", value: `${uiMoney(est.est_starting_monthly_revenue, { compact: true })}/mo` },
        { label: "Growth", value: `${est.est_monthly_growth_rate_pct.toFixed(2)}%/mo` },
        { label: "Gross margin", value: `${est.est_gross_margin_pct.toFixed(1)}%`, sub: `cost of goods ${est.cogs_pct_of_revenue.toFixed(1)}% of revenue` },
        { label: "Launch boost", value: est.marketing_budget > 0 ? `+${est.launch_marketing_boost_pct.toFixed(1)}%/mo` : "—" },
        { label: "Product sophistication", value: `${est.product_tier_pct}/100`, sub: est.configuration_tier },
      ])}
      <div class="sx-findings" style="margin-top:12px;">${warnings.map(uiFinding).join("")}</div>`;
    $b("#lx-footnote").innerHTML = `Total launch cost: <b>${uiMoney(est.total_launch_cost)}</b> (${uiMoney(est.development_investment)} development + ${uiMoney(est.marketing_budget)} marketing)`;
  };
  const renderReview = async () => {
    $b("#lx-sub").textContent = "Step 4: name it, set the budgets and confirm. Drag a slider and the backend's own launch numbers update: exactly what the launch will do.";
    $b("#lx-body").innerHTML = `
      <div class="field"><label>Product name</label><input id="lx-name" value="${esc(st.name)}" placeholder="${esc((st.codename || "Project Falcon").replace("Project ", ""))} (codename)"/></div>
      <div class="lx-review">
        <div class="lx-config">
          <div class="sx-eyebrow">Configuration</div>
          ${Object.entries(studio.selections).map(([dim, key]) => {
            const label = dim === base.defining_dimension ? base.types[Number(key)].label
              : (studio.specs.find((d) => d.dimension === dim)?.options[Number(key)]?.label || key);
            return `<div class="lx-config-row"><span>${esc(dim)}</span><b>${esc(label)}</b></div>`;
          }).join("")}
          <div class="lx-review-budgets">
            ${lxSlider("lx-inv2", "Development budget", devBudget(), base.cash, { marker: studio.required_budget, markerLabel: "needed" })}
            ${lxSlider("lx-mb2", "Marketing budget", st.marketingBudget || 0, Math.max(10000, base.cash * 0.5))}
          </div>
        </div>
        <div id="lx-est"><div class="lx-loading">Pricing the launch…</div></div>
      </div>
      <div id="lx-proto"></div>
      <div id="lx-ghost" class="sx-forecast"></div>`;
    $b("#lx-name").addEventListener("input", (e) => { st.name = e.target.value; });
    // Ghost Futures: the whole company 12 months on, with and without this launch
    const ghost = uiGhostPanel($b("#lx-ghost"));
    const ghostRun = () => ghost.run({ type: "launch_product", params: { name: (st.name || "").trim() || "New Product", investment: devBudget(), selections: studio.selections } });
    let timer = null;
    const live = () => { clearTimeout(timer); timer = setTimeout(renderEstimate, 120); ghostRun(); };
    ghostRun();
    $b("#lx-inv2").addEventListener("input", (e) => {
      st.autoBudget = false; st.investment = Number(e.target.value) || 0;
      $b("#lx-inv2-val").textContent = uiMoney(st.investment); live();
    });
    $b("#lx-mb2").addEventListener("input", (e) => {
      st.marketingBudget = Number(e.target.value) || 0;
      $b("#lx-mb2-val").textContent = uiMoney(st.marketingBudget); live();
    });
    if (typeof window.fgPrototypeCard === "function") window.fgPrototypeCard($b("#lx-proto"), studio.selections);
    await renderEstimate();
  };

  const go = async (step) => {
    st.step = step;
    renderSteps();
    $b("#lx-body").scrollTop = 0;
    if (step === 0) renderTypes();
    if (step === 1) { await reloadSpecs(); }
    if (step === 2) renderMarketing();
    if (step === 3) await renderReview();
  };
  $b("#lx-back").addEventListener("click", () => go(Math.max(0, st.step - 1)));
  $b("#lx-next").addEventListener("click", async () => {
    if (st.step < 3) return go(st.step + 1);
    const name = (st.name || "").trim() || (st.codename || "").replace("Project ", "") || "New Product";
    const btn = $b("#lx-next");
    btn.disabled = true;
    try {
      const res = await postJSON("/products/launch", {
        name, investment: devBudget(), selections: studio.selections,
        marketing_selections: st.mktSel, marketing_budget: st.marketingBudget || 0,
      });
      // built on an R&D innovation: link it (customers start happier, growth starts faster)
      if (opts.innovation && res.product_id) {
        try {
          const r2 = await postJSON("/actions/execute", { action: { type: "rnd_innovation_launched", params: { innovation_id: opts.innovation.id, product_id: res.product_id } } });
          if (r2.message) res.message = `${res.message || ""} ${r2.message}`.trim();
        } catch (e) { /* the launch itself stands */ }
      }
      close();
      await window.refreshAll();
      toast(res.message || `Launched '${name}'.`);
      // Launch Day (ui/launchday.js): the moment itself, before anything else
      if (res.product_id && typeof window.openLaunchDay === "function") await window.openLaunchDay(res.product_id);
      // The Forge: one decision grows into a family. Offer the spin-offs.
      if (res.product_id && typeof window.fgOpenVariants === "function") await window.fgOpenVariants(res.product_id, { afterLaunch: true });
    } catch (e) {
      btn.disabled = false;
      toast(e.message || "Launch failed.");
    }
  });

  try {
    [base, mktSchema] = await Promise.all([postJSON("/products/studio", {}), uiGet("/marketing/schema")]);
  } catch (e) {
    $b("#lx-body").innerHTML = `<div class="lx-loading">Couldn't load the product studio: ${esc(e.message || "")}</div>`;
    return;
  }
  st.typeIndex = base.recommended_type;
  try { base.has_products = ((await uiGet("/products")) || []).some((p) => p.active !== false && p.active !== 0); } catch (e) { base.has_products = true; }
  await go(0);
  if (opts.fast) await fastLaunch();
  $b("#lx-footnote").innerHTML = st.typeIndex !== null ? `Selected: <b>${esc(base.types[st.typeIndex].label)}</b> (best for you)` : "";
}

// Late-bound: a later view may replace these (window.X = ...), so callers use window.X.
window.openLaunchProductModal = openLaunchProductModal;

export { lxSlider, openLaunchProductModal };
